// "Alternative to X" page, e.g. /tradezella-alternative: an honest side-by-side with
// another journal (data in comparePages.js). The same text goes to search engines.
import { Link } from 'react-router-dom';
import { findComparePage } from './comparePages';
import { FIRM_PAGES } from './firmPages';
import { PublicShell, localPath, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';

export default function ComparePage({ user, slug, forceLang }) {
  const page = findComparePage(slug);
  const lang = usePageLanguage(forceLang, `/${slug}`);
  const { t } = useI18n();
  const copy = page[lang] || page.en;
  const them = page.name;
  useTitle(t('{name} Alternative: Free Trading Journal for Prop Firm Traders | Market Matrix', { name: them }));
  const startTo = user ? '/app' : '/signup';
  const startLabel = user ? t('Open journal') : t('Start your free journal');

  return (
    <PublicShell user={user} className="firm-page compare-page">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Market Matrix vs {name}', { name: them })}</p>
        <h1>{copy.title}</h1>
        <p className="subtitle">{copy.intro}</p>
        <div className="firm-cta">
          <Link to={startTo} className="cta-button">{startLabel} &rarr;</Link>
          <Link to={localPath(lang, '/')} className="firm-secondary">{t('See how it works')}</Link>
        </div>
        {!user && <p className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
      </header>

      <section className="firm-section">
        <h2>{t('Market Matrix and {name}, side by side', { name: them })}</h2>
        <div className="table-wrap">
          <table className="firm-rules compare-table">
            <thead>
              <tr>
                <th scope="col"><span className="visually-hidden">{t('Feature')}</span></th>
                <th scope="col">Market Matrix</th>
                <th scope="col">{them}</th>
              </tr>
            </thead>
            <tbody>
              {copy.rows.map(([label, ours, theirs]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  <td>{ours}</td>
                  <td>{theirs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="firm-note">
          {t('{name} details come from its own website and help center, checked {date}. Prices and features change, so check {name}’s site for the latest. “—” means we couldn’t confirm it.', { name: them, date: page.checked[lang] || page.checked.en })}
        </p>
      </section>

      <section className="firm-section compare-choose">
        <div>
          <h2>{t('Choose {name} if', { name: them })}</h2>
          <ul className="firm-watch">{copy.chooseThem.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        <div>
          <h2>{t('Choose Market Matrix if')}</h2>
          <ul className="firm-watch">{copy.chooseUs.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      </section>

      <section className="firm-section">
        <h2>{t('Switching takes a few minutes')}</h2>
        <ol className="firm-steps">
          <li><strong>{t('Add your account.')}</strong> {t('Pick your prop firm’s preset, or enter your own rules.')}</li>
          <li><strong>{t('Import your trades.')}</strong> {t('Export your history from your trading platform (TopstepX, Tradovate, NinjaTrader web or any CSV) and upload it. Your past trades and their fees come in together.')}</li>
          <li><strong>{t('Know where you stand.')}</strong> {t('See your room left, your progress to the target and what to work on, after every import.')}</li>
        </ol>
      </section>

      <section className="firm-section firm-links">
        <h2>{t('Journals by prop firm')}</h2>
        <ul>
          {FIRM_PAGES.map((p) => <li key={p.slug}><Link to={localPath(lang, `/${p.slug}`)}>{t('{firm} trading journal', { firm: p.name })}</Link></li>)}
        </ul>
      </section>

      <section className="final-cta blog-final-cta">
        <h2>{t('Try it free on your own trades')}</h2>
        <Link to={startTo} className="cta-button">{startLabel} &rarr;</Link>
        <p className="firm-note">{t('{name} is a trademark of its owner. Market Matrix isn’t affiliated with {name}. Trading futures involves substantial risk of loss.', { name: them })}</p>
      </section>
    </PublicShell>
  );
}
